/**
 * The worker protocol, and the small amount of layout the two threads must
 * agree on that is *not* generated from Rust.
 *
 * Everything describing a snapshot slot comes from `SnapshotLayout`, handed
 * across at boot — see `generated/SnapshotLayout.ts`. What lives here is the
 * JS-side control blocks: the ring's "which slot is newest" word and the
 * command mailbox's head/tail. Those are JS-owned by construction, because
 * they are written with `Atomics` and WASM linear memory is not shared.
 */

import type { FloorCommand } from './generated/FloorCommand';
import type { FloorUi } from './generated/FloorUi';
import type { CampaignCommand } from './generated/CampaignCommand';
import type { CampaignUi } from './generated/CampaignUi';
import type { Command } from './generated/Command';
import type { Pace } from './generated/Pace';
import type { Figure } from './generated/Figure';
import type { FirmUi } from './generated/FirmUi';
import type { LineUi } from './generated/LineUi';
import type { AssignmentUi } from './generated/AssignmentUi';
import type { GradeSplitUi } from './generated/GradeSplitUi';
import type { ProductGradeUi } from './generated/ProductGradeUi';
import type { SlotUi } from './generated/SlotUi';
import type { LinePointUi } from './generated/LinePointUi';
import type { ProductUi } from './generated/ProductUi';
import type { ProjectUi } from './generated/ProjectUi';
import type { PnlUi } from './generated/PnlUi';
import type { LedgerUi } from './generated/LedgerUi';
import type { TechUi } from './generated/TechUi';
import type { NodeUi } from './generated/NodeUi';
import type { MarketUi } from './generated/MarketUi';
import type { SocketUi } from './generated/SocketUi';
import type { BidUi } from './generated/BidUi';
import type { ContractUi } from './generated/ContractUi';
import type { RivalUi } from './generated/RivalUi';
import type { EventUi } from './generated/EventUi';
import type { FrameBudget } from './generated/FrameBudget';
import type { CalibrationUi } from './generated/CalibrationUi';
import type { CrewUi } from './generated/CrewUi';
import type { CostUi } from './generated/CostUi';
import type { CurvePointUi } from './generated/CurvePointUi';
import type { Dispatch } from './generated/Dispatch';
import type { DispatchTypeUi } from './generated/DispatchTypeUi';
import type { MoveUi } from './generated/MoveUi';
import type { ObjectiveUi } from './generated/ObjectiveUi';
import type { ReadingUi } from './generated/ReadingUi';
import type { ScoreUi } from './generated/ScoreUi';
import type { SessionUi } from './generated/SessionUi';
import type { FloorView } from './generated/FloorView';
import type { FlowUi } from './generated/FlowUi';
import type { RoleLoadUi } from './generated/RoleLoadUi';
import type { RefusalUi } from './generated/RefusalUi';
import type { SiteCatalogUi } from './generated/SiteCatalogUi';
import type { SnapshotLayout } from './generated/SnapshotLayout';
import type { ToolLoadUi } from './generated/ToolLoadUi';
import type { ToolModelUi } from './generated/ToolModelUi';
import type { FleetUi } from './generated/FleetUi';
import type { MachineUi } from './generated/MachineUi';
import type { Purchase } from './generated/Purchase';
import type { UsedListingUi } from './generated/UsedListingUi';
import type { UsedMarketUi } from './generated/UsedMarketUi';
import type { DivisionUi } from './generated/DivisionUi';
import type { LibraryEntryUi } from './generated/LibraryEntryUi';
import type { TemplateUi } from './generated/TemplateUi';
import type { GenerationUi } from './generated/GenerationUi';
import type { GenSlotUi } from './generated/GenSlotUi';
import type { DesignPreviewUi } from './generated/DesignPreviewUi';
import type { PreviewAttrUi } from './generated/PreviewAttrUi';
import type { Funding } from './generated/Funding';
import type { DivisionKind } from './generated/DivisionKind';

/** How snapshots reach the main thread. Both paths are real; see `ring.ts`. */
export type Transport = 'sab' | 'copy';

/* ------------------------------------------------------------------ *
 * Snapshot control block (SAB transport only)
 * ------------------------------------------------------------------ */

/** Slot index of the most recently completed publish. */
export const CTRL_LATEST_SLOT = 0;
/** Sequence number of that publish; monotonic, wraps with u32. */
export const CTRL_LATEST_SEQ = 1;
/** Total publishes since boot. */
export const CTRL_PUBLISHES = 2;
/** Bytes written by the most recent publish (header + live instances). */
export const CTRL_LAST_BYTES = 3;
/** Number of `Int32` words in the snapshot control block. */
export const CTRL_WORDS = 8;

/* ------------------------------------------------------------------ *
 * Command mailbox (SAB transport only)
 * ------------------------------------------------------------------ */

/** Monotonic count of command batches written by the main thread. */
export const CMD_HEAD = 0;
/** Monotonic count of batches consumed by the worker. */
export const CMD_TAIL = 1;
/** Batches dropped because the mailbox was full — a HUD number, not a silence. */
export const CMD_DROPPED = 2;
export const CMD_WORDS = 4;

/** Mailbox slots. Commands are player actions, so this is generous. */
export const CMD_SLOTS = 64;
/** Bytes per mailbox slot: a `u32` length followed by UTF-8 JSON. */
export const CMD_SLOT_BYTES = 2048;
/**
 * High bit of a slot's length word: this batch continues in the next slot.
 *
 * Most commands are a click's worth of JSON and fit in one slot. A layout
 * import is a *file*, and does not — B0.2 found that out the hard way, by
 * dropping one silently on the shared-memory path while the copy path took it,
 * which is exactly the divergence between transports the architecture forbids.
 * Chunking keeps one code path and keeps arrival order, which a second channel
 * for big payloads would not.
 */
export const CMD_CONTINUES = 0x8000_0000;

/* ------------------------------------------------------------------ *
 * Messages
 * ------------------------------------------------------------------ */

export interface BootMessage {
  type: 'boot';
  /** The floor's seed (B0's `seed`), and since M1.1 the economy's RNG seed. */
  seed: number;
  rngSeed: number;
  /** Agents the world allocates room for; the shift itself is content. */
  capacity: number;
  /**
   * The transport the page *wants*. The worker may answer with `copy` anyway —
   * it is the side that knows whether this context really got shared memory —
   * and `ReadyMessage.transport` is the one that counts.
   */
  transport: Transport;
}

export interface CommandMessage {
  type: 'commands';
  /** A JSON array of `CampaignCommand`, already serialized. */
  json: string;
}

/**
 * Ask whether an edit *would* be refused. A read, not a write: the worker
 * answers from the same legality function the command path runs, so what the
 * build cursor is tinted with cannot drift from what the click will do. Sent on
 * cell changes rather than per frame — a player action's cadence, not a
 * frame's.
 */
export interface ProbeMessage {
  type: 'probe';
  /** Correlates the answer with the asker. */
  id: number;
  /** A single serialized `FloorCommand`. */
  json: string;
}

/**
 * Ask the design desk what a loadout would compile to (M1.5). A read, not a
 * write: the worker answers from the same `plan_design` the `StartDesign`
 * command runs, so the preview and the tape-out cannot disagree. Sent when the
 * player edits the loadout — a form's cadence, not a frame's.
 */
export interface DesignPreviewMessage {
  type: 'designPreview';
  id: number;
  /** `{"generation": id, "node": id, "loadout": [entry|null, ...]}`. */
  json: string;
}

/** Ask for the layout as a file — the export half of the save discipline. */
export interface ExportMessage {
  type: 'export';
  id: number;
}

/**
 * Ask for one of the build's own layouts by name. Answered with the same
 * `layout` message an export is, because it is the same thing: a layout file.
 */
export interface BuiltinLayoutMessage {
  type: 'builtin';
  id: number;
  name: string;
}

/**
 * Ask for the campaign as a save document (M1.1) — one string, the whole of
 * both clocks. Answered with `saveBlob`. Not a command: a save changes nothing.
 */
export interface SaveMessage {
  type: 'save';
  id: number;
}

/**
 * Replace the campaign with a saved one. Answered with `loadResult`; a refusal
 * (foreign format, doctored body, mismatched capacity) comes back as a message
 * and the running campaign stands.
 */
export interface LoadMessage {
  type: 'load';
  id: number;
  json: string;
}

export type ToWorker =
  | BootMessage
  | CommandMessage
  | ProbeMessage
  | DesignPreviewMessage
  | ExportMessage
  | BuiltinLayoutMessage
  | SaveMessage
  | LoadMessage
  | { type: 'shutdown' };

export interface ReadyMessage {
  type: 'ready';
  layout: SnapshotLayout;
  /**
   * The site catalog: room types, tool types, and the colours the renderer
   * paints them. Content crosses once at boot, not per tick — and it crosses
   * from Rust so that no palette lives in TypeScript.
   */
  catalog: SiteCatalogUi;
  /**
   * **The frame budget** (B0.6). Stated in Rust and handed over once, like the
   * layout and the catalog, so that the panel colouring a row red and the test
   * failing CI are reading the same numbers. Nothing on this side may invent a
   * threshold of its own.
   */
  budget: FrameBudget;
  /** The transport actually in force. */
  transport: Transport;
  tickHz: number;
  /** Size of the WASM instance's linear memory, for the HUD's boundary panel. */
  wasmMemoryBytes: number;
  /**
   * Shared buffers, present only when `transport` is `sab`. The worker
   * allocates them because sizing the ring needs `SnapshotLayout`, which is
   * Rust's to state — the alternative would be a second copy of the layout
   * formula on this side, which is exactly the hand-duplication the boundary
   * rules forbid.
   */
  snapshotControl?: SharedArrayBuffer;
  snapshotRing?: SharedArrayBuffer;
  commandControl?: SharedArrayBuffer;
  commandData?: SharedArrayBuffer;
}

export interface SnapshotMessage {
  type: 'snapshot';
  slot: number;
  seq: number;
  byteLen: number;
  /** Transferred, not copied twice: this buffer is detached on the worker. */
  bytes: ArrayBuffer;
}

/** What the worker knows about its own cost. The HUD's other half. */
export interface WorkerStats {
  /** Fixed steps taken since boot. */
  steps: number;
  /** Rolling mean cost of one fixed step, in milliseconds. */
  stepMs: number;
  /** Rolling mean cost of one publish (write + hand-off), in milliseconds. */
  publishMs: number;
  /** Publishes since boot. */
  publishes: number;
  /** Bytes handed to the transport since boot. */
  bytes: number;
  /** Command batches applied since boot. */
  commandBatches: number;
  /** Steps skipped because the worker fell behind its own clock. */
  droppedSteps: number;
  /**
   * Commands applied and bytes of command JSON drained since boot (B0.6).
   *
   * Counted in the worker rather than at the sink because the sink is on the
   * *other* side of the mailbox: what the budget wants to know is what actually
   * arrived, and on the shared path those are not the same number when a batch
   * is dropped for want of room.
   */
  commands: number;
  commandBytes: number;
}

export interface TickMessage {
  type: 'tick';
  /** The floor's store, unchanged from B0. */
  ui: FloorUi;
  /** The economy's store (M1.1): the campaign, the firm, the ledger, taken apart. */
  campaign: CampaignUi;
  stats: WorkerStats;
}

export interface ErrorMessage {
  type: 'error';
  message: string;
}

/** The answer to a `probe`: `null` when the edit would be accepted. */
export interface ProbeResultMessage {
  type: 'probeResult';
  id: number;
  refusal: RefusalUi | null;
}

/** The answer to a `designPreview` (M1.5). */
export interface DesignPreviewResultMessage {
  type: 'designPreviewResult';
  id: number;
  preview: DesignPreviewUi;
}

/** The answer to an `export`. */
export interface LayoutMessage {
  type: 'layout';
  id: number;
  json: string;
}

/** The answer to a `save`. */
export interface SaveBlobMessage {
  type: 'saveBlob';
  id: number;
  json: string;
}

/** The answer to a `load`: `null` on success, else why it was refused. */
export interface LoadResultMessage {
  type: 'loadResult';
  id: number;
  error: string | null;
}

export type FromWorker =
  | ReadyMessage
  | SnapshotMessage
  | TickMessage
  | ProbeResultMessage
  | DesignPreviewResultMessage
  | LayoutMessage
  | SaveBlobMessage
  | LoadResultMessage
  | ErrorMessage;

export type {
  AssignmentUi,
  GradeSplitUi,
  ProductGradeUi,
  SlotUi,
  BidUi,
  CampaignCommand,
  CampaignUi,
  Command,
  ContractUi,
  EventUi,
  Figure,
  FirmUi,
  LedgerUi,
  LinePointUi,
  LineUi,
  MarketUi,
  NodeUi,
  Pace,
  PnlUi,
  ProductUi,
  ProjectUi,
  RivalUi,
  SocketUi,
  TechUi,
  CalibrationUi,
  CostUi,
  CrewUi,
  CurvePointUi,
  Dispatch,
  DispatchTypeUi,
  FloorCommand,
  FloorUi,
  FloorView,
  FlowUi,
  FrameBudget,
  MoveUi,
  ObjectiveUi,
  ReadingUi,
  RefusalUi,
  RoleLoadUi,
  ScoreUi,
  SessionUi,
  SiteCatalogUi,
  SnapshotLayout,
  ToolLoadUi,
  ToolModelUi,
  FleetUi,
  MachineUi,
  Purchase,
  UsedListingUi,
  UsedMarketUi,
  DivisionUi,
  LibraryEntryUi,
  TemplateUi,
  GenerationUi,
  GenSlotUi,
  DesignPreviewUi,
  PreviewAttrUi,
  Funding,
  DivisionKind,
};
